import React from "react";
import Nav from "@/components/layout/Nav";
import Footer from "@/components/layout/Footer";
import KineticHero from "@/components/site/KineticHero";
import EditorialMarquee from "@/components/site/EditorialMarquee";
import Manifesto from "@/components/site/Manifesto";
import WorkflowDiagram from "@/components/site/WorkflowDiagram";
import VisitDefinition from "@/components/site/VisitDefinition";
import ServiceComparison from "@/components/site/ServiceComparison";
import InteractiveQualifier from "@/components/site/InteractiveQualifier";
import UseCases from "@/components/site/UseCases";
import TrustProof from "@/components/site/TrustProof";
import CaseStudy from "@/components/site/CaseStudy";
import QuickFaq from "@/components/site/QuickFaq";
import ServiceArea from "@/components/site/ServiceArea";
import FaqSection from "@/components/site/FaqSection";
import FinalCta from "@/components/site/FinalCta";
import BeforeAfter from "@/components/site/BeforeAfter";
import { FAQ, BRAND } from "@/lib/site";
import { useMeta } from "@/lib/useMeta";

export default function Home() {
  useMeta({
    title: "LotWise Auto | Dealership Inventory Operations, Handled as a System",
    description:
      "LotWise Auto is a dedicated dealership inventory operation. Recurring onsite visits handle photos, vehicle details, listing builds, and cross-platform publishing so every unit is market ready.",
    canonicalPath: "/",
  });

  const origin =
    typeof window !== "undefined" ? window.location.origin : "";

  const orgSchema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "@id": `${origin}/#localbusiness`,
    name: "LotWise Auto",
    url: origin,
    description:
      "LotWise Auto is a dedicated dealership inventory operation across southeastern Ontario. Recurring onsite visits handle photos, vehicle details, listing builds, and cross-platform publishing so every dealership vehicle is market ready.",
    areaServed: [
      { "@type": "City", name: "Ottawa" },
      { "@type": "City", name: "Kingston" },
      { "@type": "City", name: "Cornwall" },
      { "@type": "City", name: "Pembroke" },
      { "@type": "City", name: "Petawawa" },
      { "@type": "City", name: "Perth" },
      { "@type": "City", name: "Arnprior" },
      { "@type": "City", name: "Renfrew" },
      { "@type": "City", name: "Carleton Place" },
      { "@type": "City", name: "Rockland" },
    ],
    address: {
      "@type": "PostalAddress",
      addressLocality: "Ottawa",
      addressRegion: "ON",
      addressCountry: "CA",
    },
    contactPoint: {
      "@type": "ContactPoint",
      contactType: "sales",
      telephone: BRAND.phone,
      email: BRAND.email,
      areaServed: "CA-ON",
      availableLanguage: ["English"],
    },
    email: BRAND.email,
    telephone: BRAND.phone,
    priceRange: "$$$",
    slogan: BRAND.tagline,
    knowsAbout: [
      "Dealership inventory operations",
      "Dealership listing management",
      "Vehicle merchandising",
      "Dealer inventory photography",
      "Cross-platform dealership publishing",
    ],
  };

  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    serviceType: "Dealership Inventory Operations",
    provider: {
      "@type": "LocalBusiness",
      name: "LotWise Auto",
      address: {
        "@type": "PostalAddress",
        addressLocality: "Ottawa",
        addressRegion: "ON",
        addressCountry: "CA",
      },
    },
    hasOfferCatalog: {
      "@type": "OfferCatalog",
      name: "LotWise Auto service models",
      itemListElement: [
        {
          "@type": "Offer",
          itemOffered: {
            "@type": "Service",
            name: "Scheduled Lot Service",
            description:
              "Recurring dealership visit that keeps inventory market ready. Two LotWise Operators onsite, photography, vehicle details, listing builds, and cross-platform publishing on cadence.",
          },
        },
        {
          "@type": "Offer",
          itemOffered: {
            "@type": "Service",
            name: "Managed Inventory Service",
            description:
              "Fully managed dealership merchandising operation. LotWise Auto owns the vehicle inventory presentation process end-to-end with a dedicated merchandising lead.",
          },
        },
      ],
    },
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQ.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  return (
    <div data-testid="home-page">
      <script
        type="application/ld+json"
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{
          __html: JSON.stringify([orgSchema, serviceSchema, faqSchema]),
        }}
      />
      <Nav variant="light" />
      <main>
        {/* 01 · Signature hero */}
        <KineticHero />

        {/* 02 · Editorial marquee */}
        <EditorialMarquee />

        {/* 03 · Numbered manifesto chapters */}
        <Manifesto />

        {/* 04 · Visual workflow (horizontal timeline) */}
        <WorkflowDiagram />

        {/* 05 · What a visit actually is */}
        <VisitDefinition />

        {/* 06 · Service model tabs + comparison */}
        <ServiceComparison />

        {/* 07 · Interactive Dealer Qualifier */}
        <InteractiveQualifier />

        {/* 08 · Before/After proof (existing) */}
        <BeforeAfter compact />

        {/* 09 · Dealer use-case scenarios */}
        <UseCases />

        {/* 10 · Named dealership case studies (renders when approved) */}
        <CaseStudy />

        {/* 11 · Trust + proof */}
        <TrustProof />

        {/* 11 · The five top questions */}
        <QuickFaq />

        {/* 12 · Service area */}
        <ServiceArea />

        {/* 13 · Full FAQ (kept for schema + long-tail SEO) */}
        <FaqSection />

        {/* 14 · Final CTA */}
        <FinalCta />
      </main>
      <Footer />
    </div>
  );
}
